/**
 * feedCache.js — IndexedDB-backed cache for web feed data
 *
 * Mirrors the Flutter DiskCacheStore pattern:
 *  - Stores API responses with TTL and stale-while-revalidate
 *  - LRU eviction at 200 entries
 *  - Path-based bulk invalidation
 *
 * Uses native IndexedDB (no Dexie dependency needed).
 */

const DB_NAME = 'connects_cache';
const DB_VERSION = 1;
const STORE_NAME = 'api_cache';
const MAX_ENTRIES = 200;

let db = null;

/**
 * Open (or create) the IndexedDB database.
 * Returns a promise that resolves to the IDBDatabase.
 */
function openDB() {
  if (db) return Promise.resolve(db);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const database = event.target.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onerror = (event) => {
      console.error('[FeedCache] IndexedDB open failed:', event.target.error);
      reject(event.target.error);
    };
  });
}

/**
 * Store an API response in the cache.
 *
 * @param {string} key - Cache key (e.g., "GET:/api/get_post_sync?page=1")
 * @param {*} data - Response data (must be structured-cloneable)
 * @param {number} ttlMs - Time-to-live in milliseconds (default: 5 min)
 */
export async function putCache(key, data, ttlMs = 5 * 60 * 1000) {
  try {
    const database = await openDB();
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const entry = {
      key,
      data,
      createdAt: Date.now(),
      expiry: Date.now() + ttlMs,
    };

    store.put(entry);

    // LRU eviction: if over MAX_ENTRIES, remove oldest
    const countReq = store.count();
    countReq.onsuccess = () => {
      if (countReq.result > MAX_ENTRIES) {
        const idx = store.index('createdAt');
        const deleteCount = countReq.result - MAX_ENTRIES;
        let deleted = 0;
        const cursor = idx.openCursor();
        cursor.onsuccess = (e) => {
          const c = e.target.result;
          if (c && deleted < deleteCount) {
            store.delete(c.primaryKey);
            deleted++;
            c.continue();
          }
        };
      }
    };
  } catch (e) {
    console.warn('[FeedCache] putCache failed:', e);
  }
}

/**
 * Get a fresh (non-expired) cache entry.
 * Returns null if not found or expired.
 */
export async function getCache(key) {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);

      req.onsuccess = () => {
        const entry = req.result;
        if (!entry) return resolve(null);
        if (Date.now() > entry.expiry) return resolve(null); // Expired
        resolve(entry.data);
      };

      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

/**
 * Get a stale cache entry (ignores TTL).
 * Used for stale-while-revalidate on network failure.
 *
 * @returns {{ data: *, age: number } | null} - data and age in ms
 */
export async function getStaleCache(key) {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);

      req.onsuccess = () => {
        const entry = req.result;
        if (!entry) return resolve(null);
        resolve({
          data: entry.data,
          age: Date.now() - entry.createdAt,
        });
      };

      req.onerror = () => resolve(null);
    });
  } catch (e) {
    return null;
  }
}

/**
 * Remove all entries whose key contains any of the given path segments.
 * Used for cross-resource invalidation.
 *
 * @param {string[]} paths - Path segments to match (e.g., ['my_profile', 'friend_list'])
 */
export async function invalidateCache(paths) {
  try {
    const database = await openDB();
    const tx = database.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const cursor = store.openCursor();

    cursor.onsuccess = (e) => {
      const c = e.target.result;
      if (c) {
        if (paths.some((p) => c.key.includes(p))) {
          store.delete(c.key);
        }
        c.continue();
      }
    };
  } catch (e) {
    console.warn('[FeedCache] invalidateCache failed:', e);
  }
}

/**
 * Remove a single cache entry.
 */
export async function removeCache(key) {
  try {
    const database = await openDB();
    const tx = database.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
  } catch (e) {
    console.warn('[FeedCache] removeCache failed:', e);
  }
}

/**
 * Clear the entire cache store.
 */
export async function clearAllCache() {
  try {
    const database = await openDB();
    const tx = database.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
  } catch (e) {
    console.warn('[FeedCache] clearAllCache failed:', e);
  }
}

// Per-endpoint TTL overrides (mirrors Flutter _customTTL)
const CUSTOM_TTL = {
  'contact_suggestion': 10 * 60 * 1000,  // 10 min
  'my_profile': 5 * 60 * 1000,           // 5 min
  'my_friend_profile': 5 * 60 * 1000,
  'friend_list': 5 * 60 * 1000,
  'get_post_sync': 5 * 60 * 1000,
  'stories': 3 * 60 * 1000,              // 3 min
};

// Paths that should never be cached
const NEVER_CACHE = [
  'notification',
  'latestConversations',
  'conversation',
  'chat',
  'meeting',
  'audio-rooms',
  'call',
];

/**
 * Get the TTL for a given URL path.
 */
export function getTTL(url) {
  for (const [path, ttl] of Object.entries(CUSTOM_TTL)) {
    if (url.includes(path)) return ttl;
  }
  return 5 * 60 * 1000; // Default 5 min
}

/**
 * Check if a URL should be cached.
 */
export function shouldCache(url) {
  return !NEVER_CACHE.some((p) => url.includes(p));
}

/**
 * Generate a cache key from an HTTP request config.
 */
export function makeCacheKey(method, url) {
  return `${method.toUpperCase()}:${url}`;
}
